import { useSubjectMember } from '@healthblocks-io/core'
import { isAfter } from 'date-fns'
import { addMonths, endOfDay, isFuture, isToday } from 'date-fns/esm'
import { useCallback } from 'react'

import { useAxiosAsync } from '@/@bothrs-react/hooks/useAxiosAsync'
import { useIntegrationAxios } from '@/modules/healthblocks/hooks/useIntegrationAxios'

import type { AxiosResponse } from 'axios'
import type { Appointment } from '../types/appointment.types'

function useAppointments() {
  const axios = useIntegrationAxios()
  const member = useSubjectMember()

  const getAppointments = useCallback(() => {
    return axios
      .get<Appointment[]>('/appointments', {
        params: { memberId: member?.id },
        cache: {
          override: true,
        },
      })
      .then((value: AxiosResponse<Appointment[]>) => {
        return value?.data
          ? {
              ...value,
              data: value?.data?.filter((appointment) => appointment.status !== 'cancelled'),
            }
          : value
      })
  }, [axios, member?.id])

  const getAppointmentById = useCallback(
    (id: number) => {
      return axios.get<Appointment>(`/appointments/${id}`, {
        params: { memberId: member?.id },
        cache: {
          override: true,
        },
      })
    },
    [axios, member?.id],
  )

  const getAppointmentsForToday = useCallback(() => {
    return getAppointments().then((value: AxiosResponse<Appointment[]>) => {
      return value?.data
        ? {
            ...value,
            data: value?.data?.filter((appointment) => isToday(new Date(appointment.start))),
          }
        : value
    })
  }, [getAppointments])

  const getUpcomingAppointment = useCallback(() => {
    return getAppointments().then((value: AxiosResponse<Appointment[]>) => {
      return {
        ...value,
        data: value?.data?.find((appointment) => {
          const startDate = new Date(appointment.start)
          const nextMonth = addMonths(endOfDay(new Date()), 1)
          return isFuture(startDate) && !isAfter(startDate, nextMonth)
        }),
      }
    })
  }, [getAppointments])

  return {
    getAppointments: useAxiosAsync(getAppointments, true),
    getAppointmentById: useAxiosAsync(getAppointmentById, true),
    getAppointmentsForToday: useAxiosAsync(getAppointmentsForToday, true),
    getUpcomingAppointment: useAxiosAsync(getUpcomingAppointment, true),

    appointmentsFetcher: getAppointments,
    appointmentByIdFetcher: getAppointmentById,
    appointmentsForTodayFetcher: getAppointmentsForToday,
    upcomingAppointmentFetcher: getUpcomingAppointment,
  }
}

export { useAppointments }
